<template>
  <div class="shadow-lg bg-white rounded p-3">
    <div
      v-if="title"
      class="d-flex justify-content-between align-items-center mb-3"
    >
      <h3 class="text-primary mb-0">{{ title }}</h3>
    </div>
    <!-- Top scrollbar div -->
    <div ref="topScrollbar" class="top-scrollbar">
      <div class="scroll-inner"></div>
    </div>

    <div ref="tableWrapper" class="table-responsive custom-scroll-x">
      <table :class="tableClasses">
        <thead>
          <tr>
            <th
              v-for="(column, index) in columns"
              :key="index"
              :style="{
                width: column.width || 'auto',
                cursor: column.sortable !== false ? 'pointer' : 'default',
              }"
              @click="
                column.sortable !== false &&
                  sortBy(column.sortKey || column.key)
              "
              :class="{
                'text-primary': sortKey === (column.sortKey || column.key),
              }"
            >
              {{ column.label }}
              <span
                v-if="
                  column.sortable !== false && (column.sortKey || column.key)
                "
              >
                <i
                  v-if="sortKey === (column.sortKey || column.key)"
                  :class="
                    sortOrders[column.sortKey || column.key] === 1
                      ? 'fas fa-arrow-up ms-1'
                      : 'fas fa-arrow-down ms-1'
                  "
                ></i>
                <i v-else class="fas fa-sort ms-1"></i>
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, rowIndex) in sortedData" :key="rowIndex">
            <td
              v-for="(column, colIndex) in columns"
              :key="colIndex"
              :style="{ width: column.width || 'auto' }"
            >
              <!-- uncomment to see the data -->
              <!-- {{ row }} -->
              <!-- Index/S.No -->
              <span v-if="column.type === 'index'">
                {{ rowIndex + 1 }}
              </span>
              <!-- Handle Images -->
              <img
                v-else-if="column.type === 'image'"
                :src="getImageUrl(row, column.key)"
                :alt="column.label"
                @error="$event.target.src = column?.default_image_url"
                class="rounded-circle"
                style="width: 40px; height: 40px; object-fit: cover"
              />

              <!-- Date -->
              <div v-else-if="column.type === 'date'">
                {{ formatToDDMMYYYY(getNestedColumnData(row, column.key)) }}
              </div>

              <!-- Handle Buttons -->
              <div v-else-if="column.type === 'buttons'">
                <template v-for="(btn, btnIndex) in column.buttons">
                  <a
                    v-if="btn.href && btn.getHref"
                    :key="btnIndex"
                    :href="btn.getHref(row)"
                    download
                    style="text-decoration: none"
                  >
                    <button
                      :class="btn.class"
                      type="button"
                      :disabled="btn.getHref(row) === '#'"
                    >
                      {{ btn.label }}
                    </button>
                  </a>
                  <button
                    v-else
                    :class="btn.class"
                    @click="btn.action(row)"
                    type="button"
                  >
                    {{ btn.label }}
                  </button>
                </template>
              </div>

              <!-- Phone  -->
              <span v-else-if="column.type === 'phone'">
                {{
                  replaceCaretWithDash(getNestedColumnData(row, column.key)) ||
                  "--"
                }}
              </span>

              <!-- Handle Merged Fields -->
              <span v-else-if="column.keys">
                <span
                  v-if="column.href"
                  class="text-primary cursor-pointer"
                  style="text-decoration: underline"
                  @click="column.getHref(row)"
                >
                  {{ getMergedColumnData(row, column.keys) || "--" }}
                </span>
                <span v-else>
                  {{ getMergedColumnData(row, column.keys) || "--" }}
                </span>
              </span>

              <!-- Normal Value -->
              <span v-else>
                <span
                  v-if="column.href"
                  class="text-primary cursor-pointer"
                  style="text-decoration: underline"
                  @click="column.getHref(row)"
                >
                  {{ getNestedColumnData(row, column.key) || "--" }}
                </span>
                <span v-else>
                  {{ getNestedColumnData(row, column.key) || "--" }}
                </span>
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
<script>
import { generateFullUrlWithToken } from "@/helpers/helper";
export default {
  name: "CustomTableView",
  props: {
    title: {
      type: String,
      default: "",
    },
    data: {
      type: Array,
      required: true,
    },
    columns: {
      type: Array,
      required: true,
    },
    striped: {
      type: Boolean,
      default: true,
    },
    bordered: {
      type: Boolean,
      default: true,
    },
    hover: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const sortOrders = {};
    this.columns.forEach((col) => {
      const key = col.sortKey || col.key;
      if (key) sortOrders[key] = 1;
    });
    return {
      sortKey: "",
      sortOrders,
    };
  },
  computed: {
    // Sort table data by sortKey
    sortedData() {
      if (!this.sortKey) {
        return this.data;
      }

      const key = this.sortKey;
      const order = this.sortOrders[key] || 1;

      return [...this.data].sort((a, b) => {
        const valA = this.getNestedColumnData(a, key);
        const valB = this.getNestedColumnData(b, key);

        const isANull = valA == null;
        const isBNull = valB == null;

        if (isANull && isBNull) return 0;
        if (isANull) return 1;
        if (isBNull) return -1;

        if (typeof valA === "string" && typeof valB === "string") {
          const aStartsWithBracket =
            valA.startsWith("[") || valA.startsWith("{");
          const bStartsWithBracket =
            valB.startsWith("[") || valB.startsWith("{");

          if (aStartsWithBracket && !bStartsWithBracket) {
            return 1 * order;
          }

          if (!aStartsWithBracket && bStartsWithBracket) {
            return -1 * order;
          }

          const comparison = valA.localeCompare(valB);
          return comparison * order;
        }

        if (valA < valB) {
          return -1 * order;
        }
        if (valA > valB) {
          return 1 * order;
        }

        return 0;
      });
    },

    tableClasses() {
      return [
        "table",
        this.striped ? "table-striped" : "",
        this.bordered ? "table-bordered border" : "",
        this.hover ? "table-hover" : "",
      ].join(" ");
    },
  },
  methods: {
    // Sort function
    sortBy(key) {
      if (!key) return;
      if (this.sortKey === key) {
        this.sortOrders[key] = this.sortOrders[key] * -1;
      } else {
        this.sortKey = key;
      }
    },
    // Retrieve data from nested object using dot notation
    getNestedColumnData(item, key) {
      if (!key || typeof key !== "string") {
        console.warn("Invalid key in getNestedColumnData:", item, key);
        return "--";
      }
      return key.split(".").reduce((acc, part) => acc && acc[part], item);
    },
    // Merge multiple fields (e.g., full name = first_name + surname)
    getMergedColumnData(item, keys) {
      return (
        keys.map((key) => this.getNestedColumnData(item, key)).join(" ") || "--"
      );
    },
    getImageUrl(row, column) {
      const url = this.getNestedColumnData(row, column);
      return generateFullUrlWithToken(url);
    },

    // Conversion Methods - Start's
    formatToDDMMYYYY(input) {
      if (!input) return "--";

      let date;

      if (input.includes("-")) {
        date = new Date(input);
      } else if (/^\d{8}$/.test(input)) {
        const year = input.slice(0, 4);
        const month = input.slice(4, 6);
        const day = input.slice(6, 8);
        date = new Date(`${year}-${month}-${day}`);
      } else {
        return "--";
      }

      if (isNaN(date)) return "--";

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      return `${day}-${month}-${year}`;
    },
    replaceCaretWithDash(input) {
      if (!input) {
        return "--";
      }
      return input.replace(/\^/g, "-");
    },
    // Conversion Methods - End's
  },
  mounted() {
    const topScrollbar = this.$refs.topScrollbar;
    const tableWrapper = this.$refs.tableWrapper;

    const table = tableWrapper.querySelector("table");
    const scrollInner = this.$refs.topScrollbar.querySelector(".scroll-inner");

    topScrollbar.addEventListener("scroll", () => {
      tableWrapper.scrollLeft = topScrollbar.scrollLeft;
    });

    tableWrapper.addEventListener("scroll", () => {
      topScrollbar.scrollLeft = tableWrapper.scrollLeft;
    });

    if (table && scrollInner) {
      scrollInner.style.width = `${table.scrollWidth}px`;
    }
  },
};
</script>

<style scoped>
.table {
  width: 100%;
  border-collapse: collapse;
}

.table th,
.table td {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}

.table th {
  user-select: none;
}

.border {
  border: 1px solid #dee2e6;
  border-radius: 5px;
}

.table-scroll-wrapper {
  position: relative;
}

.top-scrollbar {
  overflow-x: auto;
  overflow-y: hidden;
  height: 2rem;
}

.scroll-inner {
  height: 1px;
}

.table-container {
  overflow-x: auto;
}
</style>
